import api from './api';

export interface EstadoProduccionDto {
  codigoEstado: number;
  estado: string;
}

export interface MaquinaDto {
  codigoMaquina: number;
  descripcion?: string | null;
}

export interface MoldeDto {
  codigoMolde: number;
  descripcion?: string | null;
}

export interface ProduccionOrdenListItemDto {
  codigoProduccion: number;
  ordenTrabajo: number;
  codigoArticulo: string;
  descripcion?: string | null;
  cantidad?: number | null;
  cantidadProducida?: number | null;
  cantidadRechazada?: number | null;
  fechaInicio?: string | null;
  fechaFin?: string | null;
  depositoOrigen?: number | null;
  depositoDestino?: number | null;
  codigoEstado: number;
  estado?: string | null;
  puedeEditar: boolean;
  puedeConfirmar: boolean;
  puedeAnular: boolean;
}

export interface DetalleEstadoProduccionDto {
  codigoDetalleEst: number;
  codigoEstado: number;
  estado?: string | null;
  fecha?: string | null;
  detalle?: string | null;
  usuario?: string | null;
}

export interface ProduccionOrdenDto extends ProduccionOrdenListItemDto {
  codigoReceta?: string | null;
  codigoMaquina?: number | null;
  maquinaDescripcion?: string | null;
  codigoMolde?: number | null;
  moldeDescripcion?: string | null;
  observaciones?: string | null;
  nroFormularioPedido?: number | null;
  historialEstados: DetalleEstadoProduccionDto[];
}

export interface ProduccionOrdenPagedResult {
  items: ProduccionOrdenListItemDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface ConfirmarProduccionDto {
  cantidadProducida: number;
  cantidadRechazada: number;
  sustituciones?: { codigoOriginal: string; codigoSustituto: string }[];
  consumos?: {
    codigoArticulo: string;
    cantidad: number;
    lotes?: import('./loteService').LoteAsignacion[];
  }[];
  lotes?: import('./loteService').LoteAsignacion[];
  observaciones?: string;
}

export interface ConsumoPrevistoDto {
  codigoArticulo: string;
  descripcion?: string | null;
  cantidadReceta: number;
  /** Estimado según receta × batch. */
  cantidadEstimada?: number;
  /** Cantidad a consumir (real o estimada). */
  cantidadRequerida: number;
  stockDisponible: number;
  suficiente: boolean;
  /** False para SER / no inventariados: no bloquean confirmación ni descuentan stock. */
  controlaStock?: boolean;
  codigoSustitutoPermitido?: string | null;
  esAgregado?: boolean;
}

export interface PreviewConfirmacionProduccionDto {
  codigoProduccion: number;
  codigoArticulo: string;
  codigoReceta?: string | null;
  cantidadProducida: number;
  cantidadRechazada: number;
  costoMaterialesEstimado: number;
  manoObraEstimada: number;
  costoUnitarioEstimado: number;
  consumos: ConsumoPrevistoDto[];
}

export interface ConfirmarProduccionResultDto {
  orden: ProduccionOrdenDto;
  costoMateriales: number;
  manoObra: number;
  costoUnitario: number;
}

export interface DesvioConsumoLineaDto {
  codigoArticulo: string;
  descripcion?: string | null;
  cantidadEstimada: number;
  cantidadReal: number;
  desvio: number;
  desvioPorcentaje?: number | null;
  costoUnitario: number;
  costoEstimado: number;
  costoReal: number;
  desvioPesos: number;
  origen: string;
}

export interface DesviosConsumoProduccionDto {
  codigoProduccion: number;
  codigoArticulo: string;
  codigoReceta?: string | null;
  cantidadProducida: number;
  costoTotalEstimado: number;
  costoTotalReal: number;
  desvioTotalPesos: number;
  desvioTotalPorcentaje?: number | null;
  lineas: DesvioConsumoLineaDto[];
}

const normalizePaged = (data: Record<string, unknown>): ProduccionOrdenPagedResult => ({
  items: (data.items ?? data.Items ?? []) as ProduccionOrdenListItemDto[],
  totalCount: Number(data.totalCount ?? data.TotalCount ?? 0),
  pageNumber: Number(data.pageNumber ?? data.PageNumber ?? 1),
  pageSize: Number(data.pageSize ?? data.PageSize ?? 10),
  totalPages: Number(data.totalPages ?? data.TotalPages ?? 0),
});

const produccionService = {
  getEstados: async (): Promise<EstadoProduccionDto[]> => {
    const resp = await api.get('/api/Produccion/estados');
    return (resp.data ?? []) as EstadoProduccionDto[];
  },

  getMaquinas: async (searchTerm?: string): Promise<MaquinaDto[]> => {
    const resp = await api.get('/api/Produccion/maquinas', { params: { searchTerm } });
    return (resp.data ?? []) as MaquinaDto[];
  },

  getMoldes: async (searchTerm?: string): Promise<MoldeDto[]> => {
    const resp = await api.get('/api/Produccion/moldes', { params: { searchTerm } });
    return (resp.data ?? []) as MoldeDto[];
  },

  getOrdenes: async (params: {
    pageNumber?: number;
    pageSize?: number;
    searchTerm?: string;
    codigoEstado?: number;
  }): Promise<ProduccionOrdenPagedResult> => {
    const resp = await api.get('/api/Produccion', {
      params: {
        PageNumber: params.pageNumber ?? 1,
        PageSize: params.pageSize ?? 10,
        SearchTerm: params.searchTerm || undefined,
        CodigoEstado: params.codigoEstado,
      },
    });
    return normalizePaged(resp.data as Record<string, unknown>);
  },

  getOrden: async (codigoProduccion: number): Promise<ProduccionOrdenDto> => {
    const resp = await api.get(`/api/Produccion/${codigoProduccion}`);
    return resp.data as ProduccionOrdenDto;
  },

  create: async (dto: {
    codigoArticulo: string;
    cantidad: number;
    codigoMaquina?: number | null;
    codigoMolde?: number | null;
    depositoOrigen: number;
    depositoDestino: number;
    observaciones?: string;
    nroFormularioPedido?: number | null;
    ordenTrabajo?: number | null;
  }): Promise<ProduccionOrdenDto> => {
    const resp = await api.post('/api/Produccion', dto);
    return resp.data as ProduccionOrdenDto;
  },

  update: async (
    codigoProduccion: number,
    dto: {
      cantidad: number;
      codigoMaquina?: number | null;
      codigoMolde?: number | null;
      depositoOrigen: number;
      depositoDestino: number;
      observaciones?: string;
      nroFormularioPedido?: number | null;
    },
  ): Promise<ProduccionOrdenDto> => {
    const resp = await api.put(`/api/Produccion/${codigoProduccion}`, dto);
    return resp.data as ProduccionOrdenDto;
  },

  iniciar: async (codigoProduccion: number): Promise<ProduccionOrdenDto> => {
    const resp = await api.post(`/api/Produccion/${codigoProduccion}/iniciar`);
    return resp.data as ProduccionOrdenDto;
  },

  anular: async (codigoProduccion: number, detalle?: string): Promise<ProduccionOrdenDto> => {
    const resp = await api.post(`/api/Produccion/${codigoProduccion}/anular`, { detalle });
    return resp.data as ProduccionOrdenDto;
  },

  previewConfirmacion: async (
    codigoProduccion: number,
    dto: ConfirmarProduccionDto,
  ): Promise<PreviewConfirmacionProduccionDto> => {
    const resp = await api.post(`/api/Produccion/${codigoProduccion}/preview-confirmacion`, dto);
    return resp.data as PreviewConfirmacionProduccionDto;
  },

  confirmar: async (
    codigoProduccion: number,
    dto: ConfirmarProduccionDto,
  ): Promise<ConfirmarProduccionResultDto> => {
    const resp = await api.post(`/api/Produccion/${codigoProduccion}/confirmar`, dto);
    return resp.data as ConfirmarProduccionResultDto;
  },

  getDesviosConsumo: async (codigoProduccion: number): Promise<DesviosConsumoProduccionDto> => {
    const resp = await api.get(`/api/Produccion/${codigoProduccion}/consumos-desvios`);
    return resp.data as DesviosConsumoProduccionDto;
  },
};

export default produccionService;
